import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Bot, Copy, Loader2, Package, Send } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/AppHeader";
import { AddressInput } from "@/components/AddressInput";
import { MapView } from "@/components/MapView";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, money } from "@/lib/api";

export const ALCOHOL_MSG = "Alcohol can't be sent as a store delivery";
const emptyAddr = { text: "", point: null, error: "" };

export const FeeTable = ({ fee, testId = "delivery-fee-table" }) => (
  <div data-testid={testId} className="divide-y divide-white/5 rounded-lg border border-white/10 text-sm">
    {fee.map((r) => (
      <div key={r.label} className={`flex justify-between px-3 py-2 ${r.label === "Total" ? "font-semibold text-amber-200" : "text-zinc-300"}`}>
        <span>{r.label}</span><span>{money(r.amount)}</span>
      </div>
    ))}
  </div>
);

const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Link copied"), () => toast.error("Couldn't copy"));

const NewDelivery = ({ user, onCreated }) => {
  const store = user.store_address;
  const [pickup, setPickup] = useState({ text: store?.address || "", point: store, error: "" });
  const [drop, setDrop] = useState(emptyAddr);
  const [f, setF] = useState({ items: "", customer_name: "", customer_phone: "", customer_email: "", notes: "", when: "now", scheduled_at: "" });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => {
    setQuote(null);
    if (!pickup.point || !drop.point) return;
    api.post("/business/deliveries/quote", { pickup: pickup.point, dropoff: drop.point }).then(({ data }) => setQuote(data)).catch((e) => toast.error(errMsg(e)));
  }, [pickup.point, drop.point]);
  const submit = async (e) => {
    e.preventDefault();
    if (!drop.point) { setDrop({ ...drop, error: drop.text ? "Address not found" : "Required" }); return; }
    setBusy(true);
    try {
      const body = { ...f, pickup: pickup.point, dropoff: drop.point, origin_url: window.location.origin,
        scheduled_at: f.when === "later" && f.scheduled_at ? new Date(f.scheduled_at).toISOString() : null };
      delete body.when;
      const { data } = await api.post("/business/deliveries", body);
      toast.success(data.paid ? "Paid with the customer's card — finding a driver" : "Payment link emailed to the customer");
      setF({ items: "", customer_name: "", customer_phone: "", customer_email: "", notes: "", when: "now", scheduled_at: "" });
      setDrop(emptyAddr);
      onCreated(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const input = (k, label, props = {}) => (
    <label className="block text-xs text-zinc-400">{label}
      <input required data-testid={`delivery-${k}-input`} className="lux-input mt-1" value={f[k]} onChange={set(k)} {...props} />
    </label>
  );
  return (
    <form onSubmit={submit} className="lux-card space-y-4 p-5 sm:p-6" data-testid="new-delivery-form">
      <p className="lux-eyebrow">New delivery · {user.business_name}</p>
      <label className="block text-xs text-zinc-400">What's being picked up
        <textarea required maxLength={1000} data-testid="delivery-items-input" className="lux-input mt-1 min-h-[70px]" value={f.items} onChange={set("items")} />
      </label>
      <p data-testid="delivery-alcohol-note" className="text-xs text-amber-400">{ALCOHOL_MSG}</p>
      <AddressInput label="Pickup (store)" value={pickup} onChange={setPickup} testId="delivery-pickup" />
      <div className="grid gap-3 sm:grid-cols-3">
        {input("customer_name", "Customer name")}
        {input("customer_phone", "Customer phone", { type: "tel", minLength: 7 })}
        {input("customer_email", "Customer email", { type: "email" })}
      </div>
      <AddressInput label="Drop-off" value={drop} onChange={setDrop} testId="delivery-dropoff" />
      <label className="block text-xs text-zinc-400">Notes (optional)
        <input data-testid="delivery-notes-input" className="lux-input mt-1" value={f.notes} onChange={set("notes")} maxLength={1000} />
      </label>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {["now", "later"].map((w) => (
          <button type="button" key={w} data-testid={`delivery-when-${w}`} onClick={() => setF({ ...f, when: w })}
            className={`rounded-full px-4 py-1.5 ${f.when === w ? "bg-amber-400 text-black" : "border border-white/10 text-zinc-300"}`}>{w === "now" ? "Now" : "Schedule"}</button>
        ))}
        {f.when === "later" && <input type="datetime-local" required data-testid="delivery-scheduled-input" className="lux-input !w-auto" value={f.scheduled_at} onChange={set("scheduled_at")} />}
      </div>
      {quote && <FeeTable fee={quote.fee} testId="delivery-quote" />}
      <p className="text-xs text-zinc-500">The customer pays this fee — charged to their card on file if they ride with us, otherwise we email them a secure payment link.</p>
      <button data-testid="delivery-submit" disabled={busy || !quote} className="btn-gold w-full sm:w-auto">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create delivery</button>
    </form>
  );
};

const DeliveryRow = ({ d }) => (
  <Link to={`/business/deliveries/${d.id}`} data-testid={`delivery-row-${d.id}`} className="flex items-center justify-between gap-3 border-b border-white/5 p-4 last:border-0 hover:bg-white/[0.02]">
    <div className="min-w-0 text-sm">
      <p className="truncate text-amber-100">{d.customer_name} · <span className="text-zinc-400">{d.items}</span></p>
      <p className="text-xs text-zinc-500">{fmtDate(d.created_at)} · <span data-testid={`delivery-status-${d.id}`}>{d.status_label}</span>{d.driver_name ? ` · ${d.driver_name}` : ""}</p>
    </div>
    <span className="shrink-0 text-sm text-amber-200">{money(d.total)}</span>
  </Link>
);

const StoreAssistant = () => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sid, setSid] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const q = text.trim();
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setText("");
    setBusy(true);
    try {
      const { data } = await api.post("/business/assistant", { message: q, session_id: sid });
      setSid(data.session_id);
      setMsgs((m) => [...m, { role: "ai", text: data.reply, ticket: data.ticket_id }]);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="lux-card flex flex-col p-5 sm:p-6" data-testid="store-assistant">
      <p className="lux-eyebrow flex items-center gap-2"><Bot className="h-4 w-4" /> Store assistant</p>
      <div className="mt-3 max-h-80 min-h-[120px] space-y-2 overflow-y-auto text-sm" data-testid="store-assistant-messages">
        {msgs.length === 0 && <p className="text-zinc-500">Ask about your deliveries, fees or how payment works.</p>}
        {msgs.map((m, i) => (
          <div key={i} className={`rounded-lg px-3 py-2 ${m.role === "user" ? "ml-8 bg-amber-400/10 text-amber-100" : "mr-8 bg-white/[0.04] text-zinc-200"}`}>
            <p className="whitespace-pre-wrap">{m.text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/^#+\s*/gm, "").replace(/^[-*]\s+/gm, "• ")}</p>
            {m.ticket && <Link to="/support" data-testid="store-assistant-ticket" className="mt-1 block text-xs text-amber-300">Support ticket opened →</Link>}
          </div>
        ))}
        {busy && <Loader2 className="h-4 w-4 animate-spin text-amber-400" />}
      </div>
      <form onSubmit={send} className="mt-3 flex gap-2">
        <input data-testid="store-assistant-input" className="lux-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask the assistant…" />
        <button data-testid="store-assistant-send" disabled={busy} className="btn-gold !px-4"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
};

export default function BusinessPortal() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/business/deliveries").then(({ data }) => setRows(data)).catch(() => setRows([])), []);
  useEffect(() => {
    if (user.business_status !== "approved") return undefined;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user.business_status, load]);
  if (user.business_status !== "approved") {
    return (
      <PortalShell>
        <div className="lux-card mx-auto max-w-lg p-6 text-center" data-testid="business-pending">
          <Package className="mx-auto h-8 w-8 text-amber-400" />
          <h1 className="mt-3 text-xl font-semibold text-amber-50">{user.business_name}</h1>
          <p className="mt-2 text-sm text-zinc-400">{user.business_status === "suspended" ? "Your store account is suspended — call 214-506-2525." : "Your store account is waiting for company approval"}</p>
        </div>
      </PortalShell>
    );
  }
  return (
    <PortalShell>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]" data-testid="business-portal">
        <div className="min-w-0 space-y-6">
          <NewDelivery user={user} onCreated={load} />
          <div className="lux-card overflow-hidden" data-testid="business-deliveries-list">
            <p className="lux-eyebrow p-4 pb-0">Deliveries</p>
            {rows?.length === 0 && <p className="p-4 text-sm text-zinc-500">No deliveries yet.</p>}
            {rows?.map((d) => <DeliveryRow key={d.id} d={d} />)}
          </div>
        </div>
        <StoreAssistant />
      </div>
    </PortalShell>
  );
}

export const DeliveryDetail = ({ d, onCancel, busy, testPrefix = "delivery", paidBanner = false }) => (
  <div className="space-y-4" data-testid={`${testPrefix}-detail`}>
    <div className="lux-card space-y-1 p-5 text-sm">
      <p className="lux-eyebrow" data-testid={`${testPrefix}-status`}>{d.status_label}</p>
      <p className="text-amber-100">{d.business_name} → {d.customer_name || "you"}</p>
      <p className="text-zinc-400">{d.items}</p>
      <p className="text-xs text-zinc-500">From {d.pickup.address} to {d.dropoff.address}{d.scheduled_at ? ` · scheduled ${fmtDate(d.scheduled_at)}` : ""}</p>
      {d.driver_name && <p data-testid={`${testPrefix}-driver`} className="text-zinc-300">Driver: {d.driver_name}{d.eta_min && ["accepted", "in_progress"].includes(d.status) ? ` · ETA ${Math.max(1, Math.round(d.eta_min))} min` : ""}</p>}
      {d.refunded && <p className="text-emerald-300">Refunded in full</p>}
    </div>
    <MapView pickup={d.pickup} stops={[d.dropoff]} route={d.route_geometry} driver={d.driver_location} className="h-72" testId={`${testPrefix}-map`} />
    <FeeTable fee={d.fee} />
    {d.paid && paidBanner && <p data-testid="track-payment-received" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">Payment received — thank you!</p>}
    <div className="flex flex-wrap gap-2">
      {d.pay_url && !onCancel && <a href={d.pay_url} data-testid={`${testPrefix}-pay-link`} className="btn-gold !py-2 text-sm">Pay {money(d.total)}</a>}
      {d.pay_url && onCancel && <button data-testid="delivery-copy-pay-link" onClick={() => copy(d.pay_url)} className="btn-outline-gold !py-2 text-sm"><Copy className="h-4 w-4" /> Copy payment link</button>}
      {onCancel && d.tracking_url && <button data-testid="delivery-copy-tracking-link" onClick={() => copy(d.tracking_url)} className="btn-outline-gold !py-2 text-sm"><Copy className="h-4 w-4" /> Copy tracking link</button>}
      {onCancel && ["awaiting_payment", "scheduled", "searching", "accepted", "arrived"].includes(d.status) && (
        <button data-testid="delivery-cancel-button" disabled={busy} onClick={onCancel} className="btn-danger !py-2 text-sm">Cancel delivery{d.paid ? " (full refund)" : ""}</button>
      )}
    </div>
    <div className="h-16 sm:hidden" />
  </div>
);

export function BusinessDeliveryPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get(`/business/deliveries/${id}`).then(({ data }) => setD(data)).catch((e) => setError(errMsg(e))), [id]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const cancel = async () => {
    setBusy(true);
    try { setD((await api.post(`/business/deliveries/${id}/cancel`)).data); toast.success("Delivery cancelled"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <PortalShell>
      <div className="mx-auto max-w-2xl">
        <Link to="/business" data-testid="delivery-back-link" className="text-sm text-amber-300">← Deliveries</Link>
        {error && <p data-testid="delivery-error" className="mt-4 text-sm text-red-300">{error}</p>}
        {d && <div className="mt-4"><DeliveryDetail d={d} onCancel={cancel} busy={busy} /></div>}
      </div>
    </PortalShell>
  );
}
